import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FileSizeExceededError } from './FileSizeExceededError';
import { InvalidMimeTypeError } from './InvalidMimeTypeError';
import { MaxRegisterExceededError } from './MaxRegisterExceededError';

export type ParsedSheet = {
  name: string;
  data: string[][];
};

const ONE_MEGA_BYTE_IN_BYTES = 1048576;
const ROW_LIMIT = 505;

export const VALID_SPREADSHEET_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroenabled.12',
];
export const VALID_SPREADSHEET_EXTENSIONS = ['.xls', '.xlsx', '.xlsm'];

// Some OSes report no MIME type (or a generic one) for .xlsm, so the extension is accepted as a fallback.
export const isValidSpreadsheetFile = (file: Pick<File, 'name' | 'type'>) =>
  VALID_SPREADSHEET_MIME_TYPES.includes(file.type.toLowerCase()) || VALID_SPREADSHEET_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

const useSpreadsheetParser = () => {
  const [parsedSheet, setParsedSheet] = useState<ParsedSheet[]>([]);
  const [sheetIsInValid, setSheetIsInValid] = useState(false);
  const [sheetError, setSheetError] = useState<Error | undefined>(undefined);

  const parseSpreadsheet = async (file: File): Promise<ParsedSheet[]> => {
    if (!isValidSpreadsheetFile(file)) {
      setSheetIsInValid(true);

      const error = new InvalidMimeTypeError();
      setSheetError(error);
      throw error;
    }

    if (file.size > ONE_MEGA_BYTE_IN_BYTES) {
      setSheetIsInValid(true);

      const error = new FileSizeExceededError();
      setSheetError(error);
      throw error;
    }

    const buffer = await file.arrayBuffer();
    // dynamic import
    const xlsx = await import('node-xlsx');
    const parsed = xlsx.parse(buffer, {
      type: 'string',
      raw: false,
    }) as ParsedSheet[];

    for (const sheet of parsed) {
      const rowCount = sheet.data.filter((item) => item.length).length;
      if (rowCount > ROW_LIMIT) {
        setSheetIsInValid(true);
        setParsedSheet([]);

        const error = new MaxRegisterExceededError();
        setSheetError(error);
        throw error;
      }
    }

    setParsedSheet(parsed);
    setSheetIsInValid(false);
    setSheetError(undefined);

    return parsed;
  };

  const useParseSpreadsheet = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<ParsedSheet[], unknown, File>({
      mutationFn: async (drivers) => parseSpreadsheet(drivers),
    });
  };

  return {
    parsedSheet,
    sheetIsInValid,
    sheetError,
    useParseSpreadsheet,
  };
};

export default useSpreadsheetParser;
